import {Table, Tooltip} from 'antd';
import {ColumnProps} from 'antd/lib/table';
import {
  ChargeEvent,
  SearchableEventFilterInput,
  useEventsQuery,
  useOnUpdateAnalyticsSubscription
} from 'api/generated';
import {TableCard} from 'components';
import {EventTypeTag} from 'components/EventTypeTag';
import deepEqual from 'deep-equal';
import {getEventMessage} from 'lib/eventUtils';
import {omitEmpty} from 'lib/omitEmpty';
import {dateFormat} from 'lib/utils';
import moment from 'moment';
import React, {useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import styled from 'styled-components';
import {EventType} from 'types';
import {useDebouncedCallback} from 'use-debounce';
import {
  DelimitedArrayParam,
  NumberParam,
  QueryParamConfig,
  StringParam,
  useQueryParam,
  useQueryParams
} from 'use-query-params';
import {EventFilters, FilterQueryParams} from './EventFilters';

type Props = {};

const EventsTable = styled(Table)`
  td {
    vertical-align: top;
  }
` as unknown as typeof Table;

export const queryFiltersSchema = {
  id: StringParam,
  objectId: StringParam,
  createdAt: DelimitedArrayParam,
  accountId: StringParam,
  acquirer: StringParam,
  processor: StringParam,
  customerEmail: StringParam,
  customerName: StringParam,
  authorizationCode: StringParam,
  orderId: StringParam,
  providerReferenceId: StringParam,
  paymentMethodLast4: StringParam,
  paymentMethodMethod: StringParam,
  sessionIp: StringParam,
  shopName: StringParam,
  masterAccountId: StringParam,
  billingPlan: StringParam
};

const textFields = ['shopName', 'customerEmail', 'customerName'];

const buildFilter = (
  queryFilters: {[key: string]: any},
  type: string
): SearchableEventFilterInput => {
  const filter: SearchableEventFilterInput = {};
  if (type === EventType.CHARGE_SUCCEEDED) {
    filter.or = [
      {type: {match: EventType.CHARGE_SUCCEEDED}},
      {type: {match: EventType.CHARGE_CAPTURED}}
    ];
  } else if (type === EventType.CHARGE_REFUNDED) {
    filter.or = [
      {type: {match: EventType.CHARGE_REFUNDED}},
      {type: {match: EventType.CHARGE_PARTIALLY_REFUNDED}},
      {type: {match: EventType.REFUND_FAILED}}
    ];
  } else if (type === 'all') {
    filter.not = {type: {match: EventType.CHARGE_EXPIRED}};
  } else if (type && type !== 'all') {
    filter.type = {match: type};
  }
  Object.entries(queryFilters).forEach(([key, value]) => {
    if (key === 'createdAt') {
      const range = value || [];
      filter.createdAt = {
        range: [moment(range[0]).startOf('day').unix(), moment(range[1]).endOf('day').unix()]
      };
      return;
    }
    if (!value) return;
    const queryType = textFields.includes(key) ? 'matchPhrase' : 'match';
    (filter as any)[key] = {[queryType]: value};
  });
  return filter;
};

export const PaymentEvents: React.FC<Props> = () => {
  const history = useHistory();
  const [_size, setSize] = useQueryParam<number | undefined>(
    'size',
    NumberParam as QueryParamConfig<number | undefined>
  );
  const [_from, setFrom] = useQueryParam<number | undefined>(
    'from',
    NumberParam as QueryParamConfig<number | undefined>
  );
  const [_type, setType] = useQueryParam<string | undefined>(
    'type',
    StringParam as QueryParamConfig<string | undefined>
  );
  const [queryFilters, setQueryFilters] = useQueryParams<{[key: string]: any}>(queryFiltersSchema);

  const size = _size || 20;
  const from = _from || 0;
  const type = _type || 'all';

  const filter = buildFilter(queryFilters, type);
  const {data, loading, refetch, previousData} = useEventsQuery({
    variables: {from, size, filter}
  });

  const update = useDebouncedCallback(refetch, 5000);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useOnUpdateAnalyticsSubscription({
    onSubscriptionData: () => update()
  });

  const tabList = [
    {
      key: 'all',
      tab: 'All'
    },
    {
      key: EventType.CHARGE_SUCCEEDED,
      tab: 'Succeeded'
    },
    {
      key: EventType.CHARGE_REFUNDED,
      tab: 'Refunded'
    },
    {
      key: EventType.CHARGE_AUTHORIZED,
      tab: 'Authorized'
    },
    {
      key: EventType.CHARGE_FAILED,
      tab: 'Failed'
    }
  ];

  const payload = data ?? previousData;
  const events = payload?.events.items || [];
  const total = payload?.events.total || undefined;

  const handleTypeChange = (type: string) => {
    setFrom(undefined, 'pushIn');
    if (type === 'all') {
      return setType(undefined, 'pushIn');
    }
    setType(type, 'pushIn');
  };

  const columns: Array<ColumnProps<ChargeEvent>> = [
    {
      title: 'Event',
      key: 'event',
      render(event: ChargeEvent) {
        return getEventMessage(event);
      }
    },
    {
      title: 'Event type',
      dataIndex: 'type',
      className: 'minimized-cell',
      width: 160,
      render(type: EventType) {
        return <EventTypeTag type={type} />;
      }
    },
    {
      title: 'Store',
      key: 'shop',
      className: 'minimized-cell',
      dataIndex: ['object', 'shop', 'name']
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      className: 'minimized-cell',
      render(date: number) {
        const anHourAgo = moment().subtract(59, 'minutes');
        const fullDate = moment.unix(date).format('lll');
        const relativeDate = moment.max(moment.unix(date), anHourAgo).isSame(anHourAgo)
          ? moment.unix(date).format(dateFormat.dateTime)
          : moment.unix(date).fromNow();
        return (
          <Tooltip placement="topLeft" title={fullDate}>
            {relativeDate}
          </Tooltip>
        );
      }
    }
  ];

  const handleFiltersChange = (values: FilterQueryParams) => {
    if (deepEqual(omitEmpty(values), omitEmpty(queryFilters))) {
      return refetch();
    }
    setFrom(undefined, 'pushIn');
    setQueryFilters(values, 'pushIn');
  };

  return (
    <>
      <EventFilters onChange={handleFiltersChange} queryFilters={queryFilters} />
      <TableCard size="small" tabList={tabList} activeTabKey={type} onTabChange={handleTypeChange}>
        <EventsTable
          scroll={{x: true}}
          dataSource={events}
          columns={columns}
          loading={loading}
          rowClassName={() => 'clickable'}
          rowKey="id"
          onRow={(event) => ({
            onClick: (e) => {
              e.preventDefault();
              const url = `/payments/${event.objectId}`;
              if (e.ctrlKey || e.metaKey) {
                window.open(url, event.objectId)?.focus();
              } else {
                history.push(url);
              }
            }
          })}
          pagination={{
            pageSize: size,
            current: from / size + 1,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total.toLocaleString()} events`,
            onChange: (page, size = 0) => setFrom((page - 1) * size, 'pushIn'),
            onShowSizeChange: (page, size) => setSize(size, 'pushIn')
          }}
        />
      </TableCard>
    </>
  );
};
